import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Button, Col, FormFeedback, Input, Label, Row, Form } from "reactstrap";
import { createReference, updateReference } from "store/actions";
import { useDispatch, useSelector } from "react-redux";

const CreateRefereceForm = ({ isEdit, isShow, refData = {}, isViewOnly }) => {
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.ReferenceReducer || {});

    const [referenceData, setReferenceData] = useState({
        id: undefined,
        first_name: "",
        last_name: "",
        phone_no: "",
        address: "",
        email: "",
        reference_code: "",
        pan_no: "",
        account_holder_name: "",
        bank_account_no: "",
        bank_ifsc: "",
        remarks: "",
        reference_type: "",
    });

    // Formik setup
    const validation = useFormik({
        enableReinitialize: true,
        initialValues: referenceData,
        validationSchema: Yup.object({
        first_name: Yup.string().required("Please Enter First Name"),
        last_name: Yup.string().required("Please Enter Last Name"),
        phone_no: Yup.string()
            .nullable()
            .test(
            "len-or-empty",
            "Phone number must be 10 digits",
            (val) => !val || /^\d{10}$/.test(val)
            ),
        email: Yup.string().email("Invalid email").nullable(),
        }),
            onSubmit: (values, { resetForm }) => {
            console.log("Formik onSubmit called with values:", values);
        try {
            if (isEdit) {
                const updateRefData = {
                    id: values.id,
                    first_name: values.first_name,
                    last_name: values.last_name,
                    phone_no: values.phone_no?.toString() || "",
                    email: values.email,
                    address: values.address,
                    reference_code: values.reference_code,
                    pan_no: values.pan_no,
                    account_holder_name: values.account_holder_name,
                    bank_account_no: values.bank_account_no,
                    bank_ifsc: values.bank_ifsc,
                    remarks: values.remarks,
                    reference_type: values.reference_type,
                };
            console.log("Dispatching updateReference:", updateRefData);
            dispatch(updateReference(updateRefData));
            resetForm();
        } else {
                const newRef = {
                    first_name: values.first_name,
                    last_name: values.last_name,
                    phone_no: values.phone_no?.toString() || "",
                    email: values.email,
                    address: values.address,
                    reference_code: values.reference_code,
                    pan_no: values.pan_no,
                    account_holder_name: values.account_holder_name,
                    bank_account_no: values.bank_account_no,
                    bank_ifsc: values.bank_ifsc,
                    remarks: values.remarks,
                    reference_type: values.reference_type,
                };
                console.log("Dispatching createReference:", newRef);
                dispatch(createReference(newRef));
                // don't auto reset in create case (optional) — if you want to reset uncomment:
                // resetForm();
                }
            } catch (err) {
                console.error("Error in onSubmit:", err);
            }
        },
    });

    // Load refData when editing or showing
    useEffect(() => {
        if (isEdit || isShow) {
            // Defensive fallback: if refData is undefined, retain current state
            setReferenceData((prev) => ({
                ...prev,
                id: refData?.id,
                first_name: refData?.first_name || "",
                last_name: refData?.last_name || "",
                phone_no: refData?.phone_no || "",
                address: refData?.address || "",
                email: refData?.email || "",
                reference_code: refData?.reference_code || "",
                pan_no: refData?.pan_no || "",
                account_holder_name: refData?.account_holder_name || "",
                bank_account_no: refData?.bank_account_no || "",
                bank_ifsc: refData?.bank_ifsc || "",
                remarks: refData?.remarks || "",
                reference_type: refData?.reference_type || "",
            }));
        }
        // include refData so changes to it update the form
    }, [isEdit, isShow, refData]);

    // Helpful debug: watch for validation errors in console
    useEffect(() => {
        if (Object.keys(validation.errors).length) {
            console.log("Formik validation errors:", validation.errors);
        }
    }, [validation.errors]);

    return (
        <div>
        {/* Pass Formik's handleSubmit directly */}
        <Form onSubmit={validation.handleSubmit}>
            <Row>
                <Col className="col-12">
                    <div className="mb-3">
                    <Label className="form-label">
                        First Name<span className="text-danger">*</span>
                    </Label>
                    <Input
                        name="first_name"
                        type="text"
                        placeholder="Add First Name"
                        disabled={isViewOnly}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.first_name || ""}
                        invalid={
                        validation.touched.first_name && validation.errors.first_name
                            ? true
                            : false
                        }
                    />
                    {validation.touched.first_name && validation.errors.first_name ? (
                        <FormFeedback type="invalid">
                        {validation.errors.first_name}
                        </FormFeedback>
                    ) : null}
                    </div>

                    <div className="mb-3">
                    <Label className="form-label">
                        Last Name<span className="text-danger">*</span>
                    </Label>
                    <Input
                        name="last_name"
                        type="text"
                        placeholder="Add Last Name"
                        disabled={isViewOnly}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.last_name || ""}
                        invalid={
                        validation.touched.last_name && validation.errors.last_name
                            ? true
                            : false
                        }
                    />
                    {validation.touched.last_name && validation.errors.last_name ? (
                        <FormFeedback type="invalid">
                        {validation.errors.last_name}
                        </FormFeedback>
                    ) : null}
                    </div>

                    <div className="mb-1">
                    <Label className="form-label">Phone No</Label>
                    <Input
                        name="phone_no"
                        type="text" // use text to avoid number-specific issues
                        placeholder="Add Phone Number"
                        onChange={(e) => {
                        const input = e.target.value.replace(/\D/g, ""); // allow only digits
                        if (input.length <= 10) {
                            // create a synthetic event for Formik
                            validation.setFieldValue("phone_no", input);
                        }
                        }}
                        disabled={isViewOnly}
                        onBlur={validation.handleBlur}
                        value={validation.values.phone_no || ""}
                        invalid={
                        validation.touched.phone_no && validation.errors.phone_no
                            ? true
                            : false
                        }
                    />
                    {validation.touched.phone_no && validation.errors.phone_no ? (
                        <FormFeedback type="invalid">
                        {validation.errors.phone_no}
                        </FormFeedback>
                    ) : null}
                    </div>

                    <div className="mb-1">
                    <Label className="form-label">Email Address</Label>
                    <Input
                        name="email"
                        type="text"
                        placeholder="Add Email Address"
                        disabled={isViewOnly}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.email || ""}
                        invalid={validation.touched.email && validation.errors.email ? true : false}
                    />
                    {validation.touched.email && validation.errors.email ? (
                        <FormFeedback type="invalid">{validation.errors.email}</FormFeedback>
                    ) : null}
                    </div>

                    <div className="mb-1">
                    <Label className="form-label">Adderss</Label>
                    <Input
                        name="address"
                        type="textarea"
                        placeholder="Add address"
                        disabled={isViewOnly}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.address || ""}
                        invalid={
                        validation.touched.address && validation.errors.address ? true : false
                        }
                    />
                    {validation.touched.address && validation.errors.address ? (
                        <FormFeedback type="invalid">{validation.errors.address}</FormFeedback>
                    ) : null}
                    </div>

                    <div className="mb-1">
                    <Label className="form-label">Sources</Label>
                    <Input
                        name="reference_type"
                        type="text"
                        placeholder="Add Sources"
                        disabled={isViewOnly}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.reference_type || ""}
                        invalid={
                        validation.touched.reference_type && validation.errors.reference_type ? true : false
                        }
                    />
                    {validation.touched.reference_type && validation.errors.reference_type ? (
                        <FormFeedback type="invalid">
                        {validation.errors.reference_type}
                        </FormFeedback>
                    ) : null}
                    </div>

                    <div className="mb-1">
                    <Label className="form-label">Reference Code</Label>
                    <Input
                        name="reference_code"
                        type="text"
                        placeholder="Add Reference Code"
                        disabled={isViewOnly}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.reference_code || ""}
                        invalid={
                        validation.touched.reference_code && validation.errors.reference_code ? true : false
                        }
                    />
                    {validation.touched.reference_code && validation.errors.reference_code ? (
                        <FormFeedback type="invalid">
                        {validation.errors.reference_code}
                        </FormFeedback>
                    ) : null}
                    </div>

                    <div className="mb-1">
                    <Label className="form-label">Pan No.</Label>
                    <Input
                        name="pan_no"
                        type="text"
                        placeholder="Add Pan No"
                        disabled={isViewOnly}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.pan_no || ""}
                        invalid={validation.touched.pan_no && validation.errors.pan_no ? true : false}
                    />
                    {validation.touched.pan_no && validation.errors.pan_no ? (
                        <FormFeedback type="invalid">{validation.errors.pan_no}</FormFeedback>
                    ) : null}
                    </div>

                    <div className="mb-1">
                    <Label className="form-label">Account Holder Name</Label>
                    <Input
                        name="account_holder_name"
                        type="text"
                        placeholder="Add Account Holder Name"
                        disabled={isViewOnly}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.account_holder_name || ""}
                        invalid={
                        validation.touched.account_holder_name && validation.errors.account_holder_name ? true : false
                        }
                    />
                    {validation.touched.account_holder_name && validation.errors.account_holder_name ? (
                        <FormFeedback type="invalid">{validation.errors.account_holder_name}</FormFeedback>
                    ) : null}
                    </div>

                    <div className="mb-1">
                    <Label className="form-label">Bank Account No</Label>
                    <Input
                        name="bank_account_no"
                        type="text"
                        placeholder="Add Bank Account No"
                        disabled={isViewOnly}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.bank_account_no || ""}
                        invalid={
                        validation.touched.bank_account_no && validation.errors.bank_account_no ? true : false
                        }
                    />
                    {validation.touched.bank_account_no && validation.errors.bank_account_no ? (
                        <FormFeedback type="invalid">{validation.errors.bank_account_no}</FormFeedback>
                    ) : null}
                    </div>

                    <div className="mb-1">
                    <Label className="form-label">Bank IFSC</Label>
                    <Input
                        name="bank_ifsc"
                        type="text"
                        placeholder="Add Bank IFSC Code"
                        disabled={isViewOnly}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.bank_ifsc || ""}
                        invalid={validation.touched.bank_ifsc && validation.errors.bank_ifsc ? true : false}
                    />
                    {validation.touched.bank_ifsc && validation.errors.bank_ifsc ? (
                        <FormFeedback type="invalid">{validation.errors.bank_ifsc}</FormFeedback>
                    ) : null}
                    </div>

                    <div className="mb-1">
                    <Label className="form-label">Remarks</Label>
                    <Input
                        name="remarks"
                        type="text"
                        placeholder="Add Remarks"
                        disabled={isViewOnly}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.remarks || ""}
                        invalid={validation.touched.remarks && validation.errors.remarks ? true : false}
                    />
                    {validation.touched.remarks && validation.errors.remarks ? (
                        <FormFeedback type="invalid">{validation.errors.remarks}</FormFeedback>
                    ) : null}
                    </div>
                </Col>
            </Row>

            <Row>
                <Col>
                    <div className="text-end">
                    <Button type="submit" color="primary" disabled={loading || isViewOnly}>
                        {loading ? "Loading" : "Save"}
                    </Button>
                    </div>
                </Col>
            </Row>
        </Form>
        </div>
    );
};

export default CreateRefereceForm;
